import { MessagingSafetyMatchMode } from '@gql'
import type { MessageValue, NoMessageValues } from 'react-intl'
import { defineMessages, type IntlShape } from 'react-intl'

export const safetyTermMessages = defineMessages<{
  readonly addAction: NoMessageValues
  readonly busyReason: NoMessageValues
  readonly createSuccess: NoMessageValues
  readonly createTitle: NoMessageValues
  readonly deleteAlternative: NoMessageValues
  readonly deleteSuccess: NoMessageValues
  readonly deleteTitle: { readonly term: MessageValue }
  readonly deleteWarning: NoMessageValues
  readonly editTitle: NoMessageValues
  readonly emptyFiltered: NoMessageValues
  readonly emptyList: NoMessageValues
  readonly enabledColumn: NoMessageValues
  readonly enabledHelp: NoMessageValues
  readonly enabledLabel: NoMessageValues
  readonly enabledToggleAria: { readonly term: MessageValue }
  readonly hitCountColumn: NoMessageValues
  readonly hitCountTooltip: NoMessageValues
  readonly hitCountUnknownTooltip: NoMessageValues
  readonly listTitle: NoMessageValues
  readonly matchModeAnywhere: NoMessageValues
  readonly matchModeAnywhereExample: NoMessageValues
  readonly matchModeColumn: NoMessageValues
  readonly matchModeLabel: NoMessageValues
  readonly matchModeWholeWord: NoMessageValues
  readonly matchModeWholeWordExample: NoMessageValues
  readonly modeAnywhereShort: NoMessageValues
  readonly modeWholeWordShort: NoMessageValues
  readonly noPermissionReason: NoMessageValues
  readonly noteColumn: NoMessageValues
  readonly noteHelp: NoMessageValues
  readonly noteLabel: NoMessageValues
  readonly noteTooLong: NoMessageValues
  readonly previewBroad: { readonly matched: number | bigint; readonly sample: number | bigint }
  readonly previewClean: { readonly sample: number | bigint }
  readonly previewEmpty: NoMessageValues
  readonly previewExamplesTitle: NoMessageValues
  readonly previewLoading: NoMessageValues
  readonly previewSome: { readonly matched: number | bigint; readonly sample: number | bigint }
  readonly previewTitle: NoMessageValues
  readonly renameNote: NoMessageValues
  readonly renameWarning: { readonly previous: MessageValue }
  readonly searchLabel: NoMessageValues
  readonly statusFilterAll: NoMessageValues
  readonly statusFilterDisabled: NoMessageValues
  readonly statusFilterEnabled: NoMessageValues
  readonly statusFilterLabel: NoMessageValues
  readonly termColumn: NoMessageValues
  readonly termHelp: NoMessageValues
  readonly termLabel: NoMessageValues
  readonly termRequired: NoMessageValues
  readonly termTooLong: NoMessageValues
  readonly testerClean: NoMessageValues
  readonly testerFlagged: { readonly count: number | bigint; readonly terms: MessageValue }
  readonly testerHelp: NoMessageValues
  readonly testerLabel: NoMessageValues
  readonly testerNoAccess: NoMessageValues
  readonly testerTitle: NoMessageValues
  readonly testerUnavailable: NoMessageValues
  readonly updateSuccess: NoMessageValues
}>({
  addAction: {
    defaultMessage: 'New term',
    description: 'safety term list — open the create dialog',
  },
  busyReason: {
    defaultMessage: 'A change is still saving.',
    description: 'safety term row action disabled reason',
  },
  createSuccess: {
    defaultMessage: 'Safety term added',
    description: 'safety term create success notification',
  },
  createTitle: {
    defaultMessage: 'New safety term',
    description: 'safety term editor dialog title',
  },
  deleteAlternative: {
    defaultMessage:
      'If it is only catching too much, turn it off instead — that is reversible and keeps the note and the count.',
    description: 'safety term delete dialog — the reversible alternative',
  },
  deleteSuccess: {
    defaultMessage: 'Safety term deleted',
    description: 'safety term delete success notification',
  },
  deleteTitle: {
    defaultMessage: 'Delete “{term}”?',
    description: 'safety term delete dialog title',
  },
  deleteWarning: {
    defaultMessage:
      'The term is removed for good. It does not unmake history — messages that already matched keep the word they recorded — and this row’s hit count goes with the row.',
    description: 'safety term delete dialog warning',
  },
  editTitle: {
    defaultMessage: 'Edit safety term',
    description: 'safety term editor dialog title',
  },
  emptyFiltered: {
    defaultMessage: 'No term matches this search.',
    description: 'safety term list empty state, with a filter applied',
  },
  emptyList: {
    defaultMessage:
      'No safety terms yet. Add one and operators are warned before a message carrying it goes out.',
    description: 'safety term list empty state',
  },
  enabledColumn: {
    defaultMessage: 'On',
    description: 'safety term list column — whether the matcher uses the term',
  },
  enabledHelp: {
    defaultMessage:
      'Off keeps the row, its note and its history but stops the matcher using it. That is the right first move on a term that turns out to catch too much — deleting is not.',
    description: 'safety term editor enabled field help',
  },
  enabledLabel: {
    defaultMessage: 'Use this term',
    description: 'safety term editor enabled field label',
  },
  enabledToggleAria: {
    defaultMessage: 'Use the term “{term}”',
    description: 'safety term list row toggle accessible name',
  },
  hitCountColumn: {
    defaultMessage: 'Hits',
    description: 'safety term list column — outbound messages that matched',
  },
  hitCountTooltip: {
    defaultMessage:
      'Outbound messages that ever matched this term, counted by the word recorded on each message.',
    description: 'safety term list hit count tooltip',
  },
  hitCountUnknownTooltip: {
    defaultMessage:
      'Not counted. This is not zero — nothing is known here about how often this term has matched.',
    description: 'safety term list tooltip for an absent hit count',
  },
  listTitle: {
    defaultMessage: 'The word list',
    description: 'safety term list card title',
  },
  matchModeAnywhere: {
    defaultMessage: 'Anywhere inside a word',
    description: 'safety term match mode option',
  },
  matchModeAnywhereExample: {
    defaultMessage:
      'Catches “asu” on its own AND inside “asuransi”, so every mention of insurance is flagged too. Check the preview before saving one of these.',
    description: 'safety term match mode option example',
  },
  matchModeColumn: {
    defaultMessage: 'Match',
    description: 'safety term list column — how the term is matched',
  },
  matchModeLabel: {
    defaultMessage: 'How it matches',
    description: 'safety term editor match mode field label',
  },
  matchModeWholeWord: {
    defaultMessage: 'Whole word only (recommended)',
    description: 'safety term match mode option',
  },
  matchModeWholeWordExample: {
    defaultMessage: 'Catches “asu” standing on its own. Leaves “asuransi” alone.',
    description: 'safety term match mode option example',
  },
  modeAnywhereShort: {
    defaultMessage: 'Anywhere',
    description: 'safety term match mode, list badge',
  },
  modeWholeWordShort: {
    defaultMessage: 'Whole word',
    description: 'safety term match mode, list badge',
  },
  noPermissionReason: {
    defaultMessage: 'Changing the word list needs the safety-policy permission.',
    description: 'safety term action disabled reason',
  },
  noteColumn: {
    defaultMessage: 'Note',
    description: 'safety term list column',
  },
  noteHelp: {
    defaultMessage:
      'Why this word is on the list. Whoever finds it over-matching in six months will want it.',
    description: 'safety term editor note field help',
  },
  noteLabel: {
    defaultMessage: 'Note (optional)',
    description: 'safety term editor note field label',
  },
  noteTooLong: {
    defaultMessage: 'A note can be at most 500 characters.',
    description: 'safety term editor validation message',
  },
  previewBroad: {
    defaultMessage:
      'Would have flagged {matched, number} of the last {sample, number} outbound messages. That is broad for a safety term — read the examples below: an everyday word probably contains it.',
    description: 'safety term preview — over-matching warning',
  },
  previewClean: {
    defaultMessage: 'None of the last {sample, number} outbound messages would have been flagged.',
    description: 'safety term preview — no matches',
  },
  previewEmpty: {
    defaultMessage:
      'Type the word above and this checks it against recent outbound traffic before you save it.',
    description: 'safety term preview placeholder',
  },
  previewExamplesTitle: {
    defaultMessage: 'What it would have caught',
    description: 'safety term preview examples heading',
  },
  previewLoading: {
    defaultMessage: 'Checking recent traffic…',
    description: 'safety term preview loading label',
  },
  previewSome: {
    defaultMessage:
      'Would have flagged {matched, number} of the last {sample, number} outbound messages.',
    description: 'safety term preview — a few matches',
  },
  previewTitle: {
    defaultMessage: 'Against recent traffic',
    description: 'safety term preview section heading',
  },
  renameNote: {
    defaultMessage:
      'Hits are counted by the word written on each message, so renaming a term leaves its history with the old spelling and starts the row again from zero.',
    description: 'safety term list footer note about renaming',
  },
  renameWarning: {
    defaultMessage:
      'Renaming resets the hit count shown in the list. Matches are counted by the word written on each message, so the history stays with “{previous}” and this row starts again from zero.',
    description: 'safety term editor warning shown while the term is being renamed',
  },
  searchLabel: {
    defaultMessage: 'Search terms',
    description: 'safety term list search field label',
  },
  statusFilterAll: {
    defaultMessage: 'All',
    description: 'safety term list status filter option',
  },
  statusFilterDisabled: {
    defaultMessage: 'Off',
    description: 'safety term list status filter option',
  },
  statusFilterEnabled: {
    defaultMessage: 'On',
    description: 'safety term list status filter option',
  },
  statusFilterLabel: {
    defaultMessage: 'Status',
    description: 'safety term list status filter label',
  },
  termColumn: {
    defaultMessage: 'Term',
    description: 'safety term list column',
  },
  termHelp: {
    defaultMessage:
      'Stored exactly as you type it. Matching ignores capitalisation, so there is no point adding the same word twice.',
    description: 'safety term editor term field help',
  },
  termLabel: {
    defaultMessage: 'Word or phrase',
    description: 'safety term editor term field label',
  },
  termRequired: {
    defaultMessage: 'Type the word or phrase this term should catch.',
    description: 'safety term editor validation message',
  },
  termTooLong: {
    defaultMessage: 'A term can be at most 120 characters.',
    description: 'safety term editor validation message',
  },
  testerClean: {
    defaultMessage: 'Nothing on the list matched this text.',
    description: 'safety term tester result',
  },
  testerFlagged: {
    defaultMessage:
      '{count, plural, one {# term matched} other {# terms matched}}: {terms}. An operator sending this would be warned first.',
    description: 'safety term tester result',
  },
  testerHelp: {
    defaultMessage:
      'Paste or type an outbound message. It is checked against the saved list as you stop typing — nothing is sent, and nothing is saved.',
    description: 'safety term tester help text',
  },
  testerLabel: {
    defaultMessage: 'Message text',
    description: 'safety term tester input label',
  },
  testerNoAccess: {
    defaultMessage:
      'Trying a message needs the messaging permission — the check runs through the same scanner the composer uses.',
    description: 'safety term tester disabled reason',
  },
  testerTitle: {
    defaultMessage: 'Try a message',
    description: 'safety term tester card title',
  },
  testerUnavailable: {
    defaultMessage:
      'The scanner could not be reached, so this text was NOT checked. That is not a clean result — try again in a moment.',
    description: 'safety term tester result when messaging-core is unreachable',
  },
  updateSuccess: {
    defaultMessage: 'Safety term saved',
    description: 'safety term update success notification',
  },
})

/** Short label for a match mode, for the list badge. */
export function translateSafetyMatchMode(intl: IntlShape, mode: MessagingSafetyMatchMode): string {
  return intl.formatMessage(
    mode === MessagingSafetyMatchMode.ANYWHERE
      ? safetyTermMessages.modeAnywhereShort
      : safetyTermMessages.modeWholeWordShort,
  )
}
